import React from "react";
import { Card, Alert } from "react-bootstrap";

function ManagingConnectors() {
  return (
    <div>
      <h2 className="mb-4">Managing Connectors</h2>

      <Alert variant="info">
        Learn how to create, edit, and test connectors for external system authentication.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>Creating a New Connector</h4>
          <ol>
            <li>Go to <strong>My Connectors</strong></li>
            <li>Click the <strong>Add Connector</strong> button</li>
            <li>Fill in the connector details:
              <ul className="mt-2">
                <li><strong>Description</strong> - What this connector is for</li>
                <li><strong>System</strong> - Select the external system type</li>
                <li><strong>Login Endpoint</strong> - The authentication URL</li>
                <li><strong>Company ID</strong> - Your organization identifier (if required)</li>
                <li><strong>Username/API Key</strong> - Authentication username or key</li>
                <li><strong>Password/Secret</strong> - Authentication password or secret</li>
              </ul>
            </li>
            <li>Click <strong>Save</strong></li>
          </ol>
          <p className="mt-3">
            <em>Note: Only Admin and Super User roles can create connectors.</em>
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Testing a Connector</h4>
          <p>
            Before using a connector in a runbook, you should test it to make sure the credentials are valid:
          </p>
          <ol>
            <li>From the connector form (when creating or editing), click <strong>Test Connection</strong></li>
            <li>The system will attempt to authenticate using the provided credentials</li>
            <li>You'll see a success message if authentication works</li>
            <li>If authentication fails, check the error message and verify your credentials</li>
          </ol>
          <p className="mt-3">
            <strong>Important:</strong> Always test connectors after creating or updating them!
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Editing a Connector</h4>
          <ol>
            <li>Go to <strong>My Connectors</strong></li>
            <li>Select the connector you want to edit</li>
            <li>Click the <strong>Edit</strong> button</li>
            <li>Update the connector details</li>
            <li>Test the connection to verify the new credentials work</li>
            <li>Click <strong>Save</strong></li>
          </ol>
          <p className="mt-3">
            <em>Note: Changes take effect immediately. Any runbooks using this connector will use the updated credentials
            on their next execution.</em>
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Common Connection Issues</h4>
          <p>If a connector test fails, check the following:</p>
          <ul>
            <li><strong>Incorrect credentials</strong> - Verify username/password or API key</li>
            <li><strong>Wrong endpoint URL</strong> - Ensure the login endpoint is correct</li>
            <li><strong>Company ID mismatch</strong> - Verify the company/organization ID</li>
            <li><strong>Network issues</strong> - The external system may be temporarily unavailable</li>
            <li><strong>Account locked/disabled</strong> - Check if the account is active in the external system</li>
            <li><strong>Permission issues</strong> - The account may not have necessary permissions</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Security Best Practices</h4>
          <ul>
            <li>Use dedicated service accounts instead of personal credentials</li>
            <li>Grant only the minimum necessary permissions to connector accounts</li>
            <li>Regularly update passwords and rotate API keys</li>
            <li>Test connectors after any credential changes</li>
            <li>Remove or disable connectors that are no longer needed</li>
            <li>Use descriptive names so you know what each connector is for</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Troubleshooting Runbook Authentication Failures</h4>
          <p>
            If a runbook execution shows "Authentication Failed" status:
          </p>
          <ol>
            <li>Go to My Connectors and find the connector the runbook was using</li>
            <li>Edit the connector and test the connection</li>
            <li>If the test fails, update the credentials</li>
            <li>Test again to verify it works</li>
            <li>Re-run the runbook</li>
          </ol>
        </Card.Body>
      </Card>
    </div>
  );
}

export default ManagingConnectors;
